import {Injectable} from '@angular/core';
import {StorageService} from './storage.service';
import {Observable, Subject} from 'rxjs';
import {ThermomixCredentials, ThermomixDecision, ThermomixToken} from '../../types/thermomix';
import {KeyMap} from '../../types/interfaces';

enum ThermomixStorage {
  CREDENTIALS = 'thermomix-credentials',
  TOKEN = 'thermomix-token',
  DECISIONS = 'thermomix-decisions'
}

/**
 * Haelt alles, was fuer den Thermomix-Sync auf dem Geraet bleibt.
 *
 * Zugangsdaten und Tokens werden ueber den StorageService abgelegt, also in der App im
 * SecureStorage und nur im Browser im localStorage. Sie werden an keiner Stelle an die
 * Kiwi-API uebertragen.
 */
@Injectable({
  providedIn: 'root'
})
export class ThermomixStorageService {

  private changeSubject: Subject<void> = new Subject<void>();

  constructor(private storageService: StorageService) { }

  /** Feuert, wenn Zugangsdaten hinterlegt oder entfernt wurden. */
  public get Change(): Observable<void> {
    return this.changeSubject.asObservable();
  }

  async saveCredentials(credentials: ThermomixCredentials) {
    await this.storageService.saveObject(ThermomixStorage.CREDENTIALS, credentials);
    this.changeSubject.next();
  }

  async getCredentials(): Promise<ThermomixCredentials> {
    return this.storageService.getObject<ThermomixCredentials>(ThermomixStorage.CREDENTIALS);
  }

  async hasCredentials(): Promise<boolean> {
    const credentials = await this.getCredentials();
    return !!(credentials?.Email && credentials?.Password);
  }

  async removeCredentials() {
    await this.storageService.remove(ThermomixStorage.CREDENTIALS, ThermomixStorage.TOKEN);
    this.changeSubject.next();
  }

  async saveToken(token: ThermomixToken) {
    return this.storageService.saveObject(ThermomixStorage.TOKEN, token);
  }

  async getToken(): Promise<ThermomixToken> {
    return this.storageService.getObject<ThermomixToken>(ThermomixStorage.TOKEN);
  }

  async removeToken() {
    return this.storageService.remove(ThermomixStorage.TOKEN);
  }

  async getDecisions(): Promise<KeyMap<ThermomixDecision>> {
    return (await this.storageService.getObject<KeyMap<ThermomixDecision>>(ThermomixStorage.DECISIONS)) || {};
  }

  async getDecision(key: string): Promise<ThermomixDecision> {
    if (!key) {
      return null;
    }
    const decisions = await this.getDecisions();
    return decisions[key] || null;
  }

  async saveDecision(decision: ThermomixDecision) {
    if (!decision?.Key) {
      return;
    }
    const decisions = await this.getDecisions();
    decisions[decision.Key] = { ...decision, UpdatedAt: Date.now() };
    return this.storageService.saveObject(ThermomixStorage.DECISIONS, decisions);
  }

  async removeDecision(key: string) {
    const decisions = await this.getDecisions();
    delete decisions[key];
    if (Object.keys(decisions).length === 0) {
      return this.storageService.remove(ThermomixStorage.DECISIONS);
    }
    return this.storageService.saveObject(ThermomixStorage.DECISIONS, decisions);
  }

  async clearDecisions() {
    return this.storageService.remove(ThermomixStorage.DECISIONS);
  }

  /** Entfernt alle Thermomix-Daten vom Geraet, z.B. beim Abmelden. */
  async clear() {
    await this.storageService.remove(
      ThermomixStorage.CREDENTIALS,
      ThermomixStorage.TOKEN,
      ThermomixStorage.DECISIONS
    );
    this.changeSubject.next();
  }
}
